import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiClock, FiCheckCircle, FiAlertCircle, FiLoader, FiUsers } from 'react-icons/fi';

const TechDashboard = () => {
  const { isTechAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, t] = await Promise.all([
          api.get('/tech-tasks/stats'),
          api.get('/tech-tasks'),
        ]);
        setStats(s.data);
        setRecent((t.data.tasks || []).slice(0, 8));
        if (isTechAdmin) {
          const m = await api.get('/tech-tasks/members');
          setMembers(m.data.users || []);
        }
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isTechAdmin]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-400" />
      </div>
    );
  }

  const cards = [
    { label: 'Open', value: stats?.pending ?? 0, icon: FiClock, color: 'text-slate-300' },
    { label: 'In progress', value: stats?.in_progress ?? 0, icon: FiLoader, color: 'text-blue-300' },
    { label: 'Done', value: stats?.completed ?? 0, icon: FiCheckCircle, color: 'text-emerald-300' },
    { label: 'Blocked', value: stats?.blocked ?? 0, icon: FiAlertCircle, color: 'text-red-300' },
  ];

  const pipelineCount = stats?.pipeline ?? 0;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Technical workspace</h1>
        <p className="text-slate-400 text-sm mt-1">Tickets, time tracking, and billable hours</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
            <c.icon className={`${c.color} mb-2`} size={20} />
            <p className="text-2xl font-bold text-white">{c.value}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-violet-900/30 border border-violet-500/20 p-4 flex items-center justify-between">
        <div>
          <p className="text-sm text-violet-200/80">Pipeline (future tasks)</p>
          <p className="text-2xl font-bold text-white mt-1">{pipelineCount}</p>
        </div>
        <Link to="/tech/pipeline" className="text-sm text-violet-300 font-medium">View pipeline →</Link>
      </div>

      <div className="rounded-2xl bg-gradient-to-r from-teal-900/40 to-slate-900 border border-teal-500/20 p-5">
        <p className="text-sm text-teal-200/80">Billable time (completed tickets)</p>
        <p className="text-3xl font-bold text-white mt-1">{stats?.billedHours ?? 0} hrs</p>
        <p className="text-xs text-slate-500 mt-1">Sum of start → end on completed work</p>
      </div>

      {isTechAdmin && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <FiUsers size={18} />
              Technical users
            </h2>
            <Link to="/tech/users" className="text-sm text-teal-400">Add member</Link>
          </div>
          {members.length === 0 ? (
            <p className="text-slate-500 text-sm">No technical members yet. Add them from Users.</p>
          ) : (
            <div className="space-y-2">
              {members.map((u) => (
                <div key={u.id} className="rounded-xl bg-slate-900 border border-slate-800 px-4 py-3 flex justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  </div>
                  <span className="text-[11px] text-teal-400 shrink-0">
                    {u.role === 'tech_admin' ? 'Tech admin' : 'Member'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-white">Recent tickets</h2>
        <Link to="/tech/tasks" className="text-sm text-teal-400">View all</Link>
      </div>
      <div className="space-y-2">
        {recent.length === 0 && (
          <p className="text-slate-500 text-sm py-8 text-center">No tickets yet. Create one from Tickets.</p>
        )}
        {recent.map((t) => (
          <Link
            key={t.id}
            to={`/tech/tasks/${t.id}`}
            className="block rounded-xl bg-slate-900 border border-slate-800 p-4 active:bg-slate-800"
          >
            <div className="flex justify-between gap-2">
              <p className="font-medium text-white truncate">{t.title}</p>
              <span className="text-xs text-slate-500 shrink-0 capitalize">{t.status.replace('_', ' ')}</span>
            </div>
            {t.duration && (
              <p className="text-xs text-teal-400 mt-1">{t.duration.label} · {t.duration.totalHours} hrs</p>
            )}
            {t.client_name && <p className="text-xs text-slate-500 mt-0.5">{t.client_name}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default TechDashboard;
